import React from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ThemeSwitch } from "../theme-switcher";
import { Button } from "../ui/button";
import { useAuthStore } from "@/stores/use-auth-store";
import { LogOutIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { GET } from "@/services/api";
import { RouteSearch } from "./route-search";

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  fixed?: boolean;
  ref?: React.Ref<HTMLElement>;
}

export const Header = ({
  className,
  fixed,
  children,
  ...props
}: HeaderProps) => {
  const nav = useNavigate();
  const [offset, setOffset] = React.useState(0);
  const { logout, user } = useAuthStore();
  const institutionId = user?.institutionId || undefined;

  const { data: institution } = useQuery({
    queryKey: ["lookups/saInstitution", institutionId],
    queryFn: () => GET(`/api/lookups/saInstitution?key=${institutionId}`),
    enabled: !!institutionId,
  });

  React.useEffect(() => {
    const onScroll = () => {
      setOffset(document.body.scrollTop || document.documentElement.scrollTop);
    };

    // Add scroll listener to the body
    document.addEventListener("scroll", onScroll, { passive: true });

    // Clean up the event listener on unmount
    return () => document.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "bg-background border-b flex h-14 items-center gap-2 px-3 sm:px-4",
        fixed && "header-fixed peer/header fixed z-50 w-[inherit]",
        offset > 10 && fixed ? "shadow-sm" : "shadow-none",
        className,
      )}
      {...props}
    >
      {/* Left: trigger + branding */}
      <div className="flex shrink-0 items-center gap-2">
        <SidebarTrigger variant="outline" />
        <img title="logo" src="/images/logo.svg" className="h-8" />
        <span className="hidden text-base font-semibold tracking-tight text-gray-600 lg:block dark:text-white">
          Central Applications Office
        </span>
      </div>

      {/* Center: search */}
      <div className="flex flex-1 justify-center px-2">
        <RouteSearch />
      </div>

      {/* Right: actions */}
      <div className="flex shrink-0 items-center gap-2">
        <ThemeSwitch />
        <div className="hidden flex-col text-right text-xs leading-tight sm:flex">
          <span className="font-medium">{user?.username} <span className="text-muted-foreground font-normal">({user?.role})</span></span>
          <span className="text-muted-foreground">
            {institution ? institution.institutionName : "All Institutions"}
          </span>
        </div>
        <Button variant="outline" size="icon" title="Log out" onClick={() => logout(nav)}>
          <LogOutIcon className="size-4" />
        </Button>
      </div>

      {children && <div className="flex items-center gap-2">{children}</div>}
    </header>
  );
};

Header.displayName = "Header";
